import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Users, Target, CalendarCheck, Mail } from 'lucide-react';

/**
 * SchoolLeadershipTeam Page
 * Overview of P.S. 155's School Leadership Team (SLT)
 */
const SchoolLeadershipTeam = () => {
  useEffect(() => { document.title = 'School Leadership Team (SLT) — PS 155 William Paca School'; }, []);

  const pillars = [
    {
      title: 'Who We Are',
      Icon: Users,
      description: 'The SLT is made up of the Principal, teachers, parents, and other school staff working together as required under the NYC Chancellor\'s Regulations.',
    },
    {
      title: 'What We Do',
      Icon: Target,
      description: 'The team develops and monitors the Comprehensive Educational Plan (CEP) and aligns the school budget with its goals for student achievement.',
    },
    {
      title: 'When We Meet',
      Icon: CalendarCheck,
      description: 'SLT meetings are held monthly during the school year. Dates and times are posted on our school calendar.',
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-neutral-800 selection:bg-school-gold selection:text-white">
      <Navbar />

      <main id="main-content" tabIndex="-1" className="outline-none">
        {/* Hero Section */}
        <section className="relative pt-32 sm:pt-40 md:pt-48 pb-12 sm:pb-14 md:pb-16 overflow-hidden bg-school-blue">
          <div className="absolute inset-0 bg-school-blue-dark"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-block py-1 px-3 rounded-full bg-school-blue text-school-gold-on-blue text-xs sm:text-sm font-bold uppercase tracking-wider mb-3 sm:mb-4 border border-white/10">
              Families
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 tracking-tight">
              School Leadership Team <span className="text-school-gold-on-blue">(SLT)</span>
            </h1>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-school-gold mx-auto mb-4 sm:mb-6 rounded-full"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto font-light leading-relaxed px-4">
              A partnership of parents and staff that shapes P.S. 155's goals and priorities.
            </p>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-10 sm:py-11 md:py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {pillars.map((pillar, index) => (
                <div
                  key={index}
                  className="bg-neutral-50 rounded-2xl p-6 sm:p-8 shadow-lg border border-neutral-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-blue-50 text-school-blue rounded-full flex items-center justify-center mb-4 sm:mb-5">
                    <pillar.Icon className="w-7 h-7" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-school-blue mb-2 sm:mb-3">
                    {pillar.title}
                  </h2>
                  <p className="text-neutral-600 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Get Involved Section */}
        <section className="py-16 sm:py-20 bg-school-blue relative overflow-hidden">
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Get Involved
            </h2>
            <p className="text-lg sm:text-xl text-blue-100 mb-8 sm:mb-10 leading-relaxed max-w-2xl mx-auto">
              Families interested in joining the School Leadership Team or learning more can reach out to our Parent Coordinator.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:mstevenson@ps155.org"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-school-gold text-school-blue-dark rounded-full font-bold text-base sm:text-lg shadow-lg hover:bg-yellow-500 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <Mail className="w-5 h-5" strokeWidth={2} />
                Contact Parent Coordinator
              </a>
              <a
                href="/calendar"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-white text-white rounded-full font-bold text-base sm:text-lg hover:bg-white hover:text-school-blue transition-all duration-300"
              >
                View Meeting Dates
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SchoolLeadershipTeam;
